import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table";
import {
  Anchor,
  Flex,
  Text,
  TextInput,
  Paper,
  Card,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconSearch, IconRefresh } from "@tabler/icons-react";
import toast from "react-hot-toast";

import { postApi } from "../../api/API";

export interface InvoiceRecord {
  id: number;
  created_at: number;
  invoice_url: string;
  invoice_id: string;
  order_id: string;
  type: string;
  extension_rzp_id: string;
}

interface FilterModel {
  order_id?: string;
}

const fetchInvoices = async (
  page: number,
  pageSize: number,
  filter: FilterModel
) => {
  const res = await postApi<any>({
    url: "/fetch/zoho/invoices",
    type: "new_admin",
    body: {
      page,
      per_page: pageSize,
      ...filter,
    },
  });
  return res;
};

const AdminInvoices = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [orderId, setOrderId] = useState("");
  const [debouncedOrderId, setDebouncedOrderId] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedOrderId(orderId);
    }, 400);
    return () => clearTimeout(t);
  }, [orderId]);

  const { data, isLoading, isRefetching, isError, refetch } = useQuery({
    queryKey: [
      "admin-invoices",
      pagination.pageIndex,
      pagination.pageSize,
      debouncedOrderId,
    ],
    queryFn: () =>
      fetchInvoices(pagination.pageIndex + 1, pagination.pageSize, {
        order_id: debouncedOrderId || undefined,
      }),
  });

  const records: InvoiceRecord[] = data?.items ?? data?.data ?? [];
  const rowCount = data?.itemsTotal ?? data?.total ?? 0;
  const pageCount = data?.pageTotal ?? 0;

  const columns = useMemo<MRT_ColumnDef<InvoiceRecord>[]>(
    () => [
      {
        accessorKey: "order_id",
        header: "Order ID",
        size: 220,
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 140,
      },
      {
        accessorKey: "invoice_url",
        header: "Invoice",
        size: 160,
        Cell: ({ row }) => (
          <Anchor
            href={row.original.invoice_url}
            target="_blank"
            rel="noreferrer"
            fw={600}
          >
            View Invoice
          </Anchor>
        ),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: records,
    manualPagination: true,
    pageCount,
    rowCount,
    enableSorting: false,
    enableColumnFilters: false,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading,
      showProgressBars: isRefetching,
      showAlertBanner: isError,
    },
    mantineTableContainerProps: {
      sx: {
        minHeight: "450px",
        borderRadius: "8px",
        border: "1px solid #e9ecef",
      },
    },
    mantinePaperProps: {
      shadow: "sm",
      radius: "md",
      withBorder: false,
    },
    enableTopToolbar: false,
  });

  useEffect(() => {
    if (isError) toast.error("Error fetching invoices");
  }, [isError]);

  return (
    <Card withBorder radius="md" p="md">
      <Paper withBorder p="md" radius="md" mb="md">
        <Flex justify="space-between" align="center" gap="md">
          <Text fw={700} fz="lg">
            Invoices
          </Text>

          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by Order ID"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              w={280}
            />

            <Tooltip label="Refresh">
              <ActionIcon
                variant="light"
                color="blue"
                onClick={() => refetch()}
                loading={isRefetching}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Flex>
      </Paper>

      <MantineReactTable table={table} />
    </Card>
  );
};

export default AdminInvoices;
